import React from "react";
import { View } from "react-native";
import { useAuth } from "lib/hooks/useAuth";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  useModal,
  Link,
  Text,
  useResponsive,
  theme,
  formatCurrency,
  Panel,
  Icon,
} from "@clearabee/ui-library-native";
import { Button } from "components/Core/Button/Button";
import { Sidebar } from "components/Sidebar/Sidebar";
import { Layout } from "components/Layout/Layout";
import { LayoutProps } from "components/Layout/Layout.types";
import { extractPossibleNumber } from "lib/utils";
import { styles } from "./AccountLayout.styles";
import { User } from "lib/auth";

interface AccounLayoutProps
  extends Omit<LayoutProps, "leftElement" | "contentWidth"> {
  tab: typeof items[number]["routeName"];
  user: User;
}

const items = [
  {
    icon: "Home",
    routeName: "Dashboard",
    friendlyName: "hwa.screens.dashboard.title",
  },
  {
    icon: "Account",
    routeName: "MyAccount",
    friendlyName: "hwa.screens.myAccount.title",
  },
  {
    icon: "Clock",
    routeName: "JobHistory",
    friendlyName: "hwa.screens.jobHistory.title",
  },
] as const;

const WelcomeBack = ({ user }: { user: User }): React.ReactElement | null => {
  const [translate] = useTranslation();
  const { isLoading } = useAuth();

  const beeLoyalExpire = user.getAttribute("bee_loyal_end");
  const hasBeeLoyalExpire = typeof beeLoyalExpire === "string";
  const beeLoyalCredit =
    extractPossibleNumber(user.getAttribute("bee_loyal_credit")) ?? 0;
  const beeLoyalMemberAttribute = user.getAttribute("bee_loyal_member");
  const isBeeloyalMember =
    typeof beeLoyalMemberAttribute === "string"
      ? beeLoyalMemberAttribute === "true"
      : !!beeLoyalMemberAttribute;

  return (
    <>
      <Text fontSize="xlarge" color="light">
        {translate("common.hey")} {user.firstName}!
      </Text>
      <Text
        fontSize="xlarge"
        color="light"
        styles={{ fontFamily: "ProximaSemiBold" }}
      >
        {translate("common.welcomeBack")}
      </Text>

      {isBeeloyalMember && (
        <>
          <Text
            fontSize="xsmall"
            color="brand.lighter"
            styles={{ marginTop: theme.spacing.xsmall, display: "flex" }}
          >
            {translate("common.beeloyalCredits")}:{" "}
            {isLoading && (
              <View
                style={{
                  marginLeft: theme.spacing.xsmall,
                }}
              >
                <Icon.Loading size="small" />
              </View>
            )}
            {!isLoading && formatCurrency(beeLoyalCredit)}
          </Text>
          {hasBeeLoyalExpire && (
            <Text
              fontSize="xsmall"
              color="brand.lighter"
              styles={{ marginTop: theme.spacing.xsmall, display: "flex" }}
            >
              {translate("common.beeloyalCreditsExpire")}:{" "}
              {isLoading && (
                <View
                  style={{
                    marginLeft: theme.spacing.xsmall,
                  }}
                >
                  <Icon.Loading size="small" />
                </View>
              )}
              {!isLoading && dayjs(beeLoyalExpire).format("DD/MM/YYYY")}
            </Text>
          )}
        </>
      )}
    </>
  );
};

export const AccountLayout = ({
  tab,
  children,
  user,
  ...layout
}: AccounLayoutProps): React.ReactElement => {
  const { signOut } = useAuth();
  const [translate] = useTranslation();
  const [Modal, showModal] = useModal();
  const { screens } = useResponsive();

  const sidebar = (
    <Sidebar
      initialTab={tab}
      tabs={[
        ...items.map(({ icon, friendlyName, ...redirect }) => ({
          tab: redirect.routeName,
          title: translate(friendlyName),
          icon: icon,
          redirect,
        })),
        ...(screens.large
          ? ([
              {
                tab: "Logout",
                title: translate("common.logout"),
                icon: "Logout",
                onClick: () => showModal(true),
                styles: styles.logout,
              },
            ] as const)
          : []),
      ]}
    />
  );

  return (
    <Layout
      {...layout}
      leftElement={
        screens.large && (
          <View style={{ position: "relative", zIndex: 1 }}>
            <View style={styles.sidebarBackground} />
            <View style={styles.welcomeWrapper}>
              <View style={styles.largeWelcomeBack}>
                <WelcomeBack user={user} />
              </View>
            </View>
            {sidebar}
            <View style={styles.cornerWrapper}>
              <View style={styles.corner} />
            </View>
          </View>
        )
      }
    >
      {!screens.large && (
        <Panel styles={styles.panel}>
          <View>
            <WelcomeBack user={user} />
          </View>

          <Button
            onClick={() => showModal(true)}
            color="negative"
            size="small"
            styles={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <View style={{ marginRight: theme.spacing.xsmall2 }}>
              <Icon.Logout size="medium" />
            </View>
            {translate("common.logout")}
          </Button>
        </Panel>
      )}

      {!screens.large && sidebar}
      {children}

      <Modal width={400} onClose={() => showModal(false)}>
        <View style={styles.modalIcon}>
          <Icon.Logout />
        </View>
        <Text color="brand" styles={{ fontFamily: "ProximaSemiBold" }}>
          {translate("common.logout")}
        </Text>
        <Text fontSize="small">
          {translate("hwa.components.accountLayout.logoutModal.heading")}
        </Text>

        <View style={styles.modalActions}>
          <Link
            color="negative"
            textStyles={theme.fontDefaults.small}
            onPress={() => showModal(false)}
            styles={{ marginRight: theme.spacing.small }}
            underline
          >
            {translate("common.cancel")}
          </Link>
          <Button size="medium" color="warning" onClick={signOut}>
            {translate("hwa.components.accountLayout.logoutModal.yesLogout")}
          </Button>
        </View>
      </Modal>
    </Layout>
  );
};
